import { useEffect } from 'react';
import { Company } from 'src/types/Companies';
import { CLIENT_COMPANY_MAPPINGS } from '../constants/clientCompanyMappings';

interface AuthTokenInfo {
  clientId: string;
  companyId: string;
  companyName: string;
}

interface NavbarError {
  type: 'localStorage' | 'context' | 'validation';
  message: string;
}

interface UseCompanyInitializationProps {
  isUserSelected: boolean;
  authTokenInfo: AuthTokenInfo | null;
  company: Company | null;
  getStoredClientId: () => string | null;
  setStoredClientId: (clientId: string | null) => void;
  clearError: () => void;
  setError: (error: NavbarError) => void;
  changeCompany: (company: Company) => void;
  dispatchClientIdChange: (clientId: string) => void;
  setSelectedClientId: (clientId: string) => void;
  setIsUserSelected: (isUserSelected: boolean) => void;
}

/**
 * Custom hook to handle company initialization with multiple priority levels:
 * 1. Auth token matching (highest priority)
 * 2. LocalStorage restoration (medium priority)
 * 3. Company context matching (fallback)
 * 4. Empty selection (default)
 */
export const useCompanyInitialization = ({
  isUserSelected,
  authTokenInfo,
  company,
  getStoredClientId,
  setStoredClientId,
  clearError,
  setError,
  changeCompany,
  dispatchClientIdChange,
  setSelectedClientId,
  setIsUserSelected,
}: UseCompanyInitializationProps) => {
  // Priority 1: Match by auth token
  useEffect(() => {
    if (isUserSelected) return;

    if (!authTokenInfo?.clientId) return;

    clearError();

    // Try to match by clientId first (most accurate)
    let authMapping = CLIENT_COMPANY_MAPPINGS.find(
      (m) => m.clientId === authTokenInfo.clientId && m.clientId !== ''
    );

    // If no clientId match, try to match by companyId
    if (!authMapping && authTokenInfo.companyId) {
      authMapping = CLIENT_COMPANY_MAPPINGS.find(
        (m) => m.companyId === authTokenInfo.companyId && m.clientId !== ''
      );
    }

    if (authMapping) {
      setSelectedClientId(authMapping.clientId);
      setIsUserSelected(false);

      const companyData: Company = {
        companyId: authMapping.companyId,
        companyName: authMapping.companyName,
      };

      try {
        changeCompany(companyData);
        setStoredClientId(authMapping.clientId);
        dispatchClientIdChange(authMapping.clientId);
      } catch (err) {
        console.error('Failed to change company during auto-selection:', err);
        setError({ type: 'context', message: 'Failed to update company context' });
      }
    }
  }, [
    isUserSelected,
    authTokenInfo?.clientId,
    authTokenInfo?.companyId,
    clearError,
    setSelectedClientId,
    setIsUserSelected,
    changeCompany,
    setStoredClientId,
    dispatchClientIdChange,
    setError,
  ]);

  // Priority 2: Restore from localStorage
  useEffect(() => {
    if (isUserSelected) return;
    if (authTokenInfo?.clientId) return; // Skip if auth token handled it

    clearError();

    const storedClientId = getStoredClientId();
    if (!storedClientId) return;

    // Validate stored clientId against available mappings
    const mapping = CLIENT_COMPANY_MAPPINGS.find(
      (m) => m.clientId === storedClientId && m.clientId !== ''
    );

    if (mapping) {
      setSelectedClientId(storedClientId);
      setIsUserSelected(false);
      dispatchClientIdChange(storedClientId);
    } else {
      // Stored clientId is no longer valid, clean up
      setStoredClientId(null);
      setError({
        type: 'validation',
        message: 'Previously selected company is no longer available',
      });
    }
  }, [
    isUserSelected,
    authTokenInfo?.clientId,
    getStoredClientId,
    clearError,
    setSelectedClientId,
    setIsUserSelected,
    dispatchClientIdChange,
    setStoredClientId,
    setError,
  ]);

  // Priority 3: Match by company context (fallback)
  useEffect(() => {
    if (isUserSelected) return;
    if (authTokenInfo?.clientId) return; // Skip if auth token handled it

    const storedClientId = getStoredClientId();
    if (storedClientId) return; // Skip if localStorage handled it

    if (!company?.companyId) return;

    clearError();

    const authMapping = CLIENT_COMPANY_MAPPINGS.find(
      (m) => m.companyId === company.companyId && m.clientId !== ''
    );

    if (authMapping) {
      setSelectedClientId(authMapping.clientId);
      setIsUserSelected(false);

      const companyData: Company = {
        companyId: authMapping.companyId,
        companyName: authMapping.companyName,
      };

      try {
        changeCompany(companyData);
        setStoredClientId(authMapping.clientId);
        dispatchClientIdChange(authMapping.clientId);
      } catch (err) {
        console.error('Failed to change company during auto-selection:', err);
        setError({ type: 'context', message: 'Failed to update company context' });
      }
    }
  }, [
    isUserSelected,
    authTokenInfo?.clientId,
    getStoredClientId,
    company?.companyId,
    clearError,
    setSelectedClientId,
    setIsUserSelected,
    changeCompany,
    setStoredClientId,
    dispatchClientIdChange,
    setError,
  ]);

  // Priority 4: Default to empty selection
  useEffect(() => {
    if (isUserSelected) return;
    if (authTokenInfo?.clientId) return;

    const storedClientId = getStoredClientId();
    if (storedClientId) return;

    if (company?.companyId) {
      const hasMapping = CLIENT_COMPANY_MAPPINGS.some(
        (m) => m.companyId === company.companyId && m.clientId !== ''
      );
      if (hasMapping) return;
    }

    // Only set empty if nothing else worked
    setSelectedClientId('');
    setIsUserSelected(false);
  }, [
    isUserSelected,
    authTokenInfo?.clientId,
    getStoredClientId,
    company?.companyId,
    setSelectedClientId,
    setIsUserSelected,
  ]);
};
